import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useChannelStateContext } from 'stream-chat-react'
import { createStyles } from '@codeleap/styles'
import { Icon } from '@/components'

const DISTANCE_FROM_BOTTOM_THRESHOLD = 100

// @spec FR-0077/AC-6@v1 — scroll-to-latest inside the open Thread.
export const ThreadScrollToBottomButton = () => {
  const { thread } = useChannelStateContext()
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true)
  const listElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!thread) {
      setContainer(null)
      listElementRef.current = null
      setIsScrolledToBottom(true)
      return
    }

    // Thread DOM mounts a few frames after the store flips, so poll the
    // selector instead of betting on a single rAF. Same pattern as the channel
    // scroll button — Stream doesn't expose a list ref.
    let polledRaf = 0
    let attachedList: HTMLElement | null = null
    let attachedHandler: (() => void) | null = null
    const tryAttach = () => {
      const threadContainer = document.querySelector<HTMLElement>('.str-chat__thread')
      const list = threadContainer?.querySelector<HTMLElement>('.str-chat__list')
      if (!threadContainer || !list) {
        polledRaf = requestAnimationFrame(tryAttach)
        return
      }
      setContainer(threadContainer)
      listElementRef.current = list
      const checkScroll = () => {
        const distance = list.scrollHeight - list.scrollTop - list.clientHeight
        setIsScrolledToBottom(distance < DISTANCE_FROM_BOTTOM_THRESHOLD)
      }
      list.addEventListener('scroll', checkScroll, { passive: true })
      checkScroll()
      attachedList = list
      attachedHandler = checkScroll
    }
    polledRaf = requestAnimationFrame(tryAttach)

    return () => {
      cancelAnimationFrame(polledRaf)
      if (attachedList && attachedHandler) {
        attachedList.removeEventListener('scroll', attachedHandler)
      }
    }
  }, [thread])

  const handleClick = useCallback(() => {
    const list = listElementRef.current
    if (!list) return
    list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
  }, [])

  if (!thread || !container || isScrolledToBottom) return null

  return createPortal(
    <button
      type='button'
      css={styles.button}
      onClick={handleClick}
      aria-label='Scroll to latest reply'
    >
      <Icon name='chevron-down' style={styles.icon} />
    </button>,
    container,
  )
}

const styles = createStyles(theme => ({
  button: {
    position: 'absolute',
    // Lifted above the thread MessageInput (Stream renders the input at the
    // bottom of `.str-chat__thread` with its own padding).
    bottom: theme.spacing.value(12),
    right: theme.spacing.value(3),
    width: 44,
    height: 44,
    borderRadius: 22,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: theme.colors.neutralSolid0,
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: theme.colors.neutralSolid100,
    },
  },
  icon: {
    width: 20,
    height: 20,
    color: theme.colors.textBody,
  },
}))
